import {
  Component,
  ElementRef,
  EventEmitter,
  OnInit,
  ViewChild,
  Output,
  Input,
  OnDestroy,
} from '@angular/core';
import { Observable, of, Subscriber, Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { PlaceSetService } from 'src/app/services/place-set.service';
import { UIPlaceState } from 'src/app/_models/state/placeState';
import { PlaceSetType, UIPlace } from 'src/app/_models/state/placeSetState';
import { ListItem } from '../favorite-place/favorite-place.component';
import { SubscriptionManager } from 'src/app/_helpers/subscription-manager';
import { StopLocation } from 'src/app/_models/state/stopState';
import { AddStopMarkerService } from 'src/app/services/add-stop-marker.service';
import { GoogleAnalytics } from 'src/app/services/google-analytics.service';

@Component({
  selector: 'app-custom-place',
  templateUrl: './custom-place.component.html',
  styleUrls: ['./custom-place.component.scss'],
})
export class CustomPlaceComponent implements OnInit, OnDestroy {
  @Output() selectedPlace: EventEmitter<UIPlaceState[]> = new EventEmitter();
  @Input() title?: string = 'Select a Custom Place Sets';
  @Input() width?: number = 360;
  customPlaces: UIPlaceState[];
  isLoadingResults: boolean;
  @ViewChild('search') search: ElementRef;
  places: UIPlace[];
  listItems: ListItem[] = [];
  itemLevel = 0;
  selectedCustomPlace: ListItem;
  selectedValue: string;
  placeLists: Observable<ListItem[]>;
  private sm = new SubscriptionManager();
  userSubscription: Subscription;
  hasResult: boolean;

  constructor(
    private placeSetService: PlaceSetService,
    private addStopMarkerService: AddStopMarkerService,
    private googleAnalytics: GoogleAnalytics
  ) {}

  ngOnInit(): void {
    this.getCustomPlaces();
  }

  showLocation(item: ListItem, $event: any) {
    $event.stopPropagation();
    const location = this.places
      .find((place) => place.Places.find((custom) => custom?.Id === item?.id))
      .Places?.find((customPlace) => customPlace?.Id === item?.id);
    const stopLocation: StopLocation = {
      name: location?.PlaceName,
      address: {
        streetAddress: location.Address.StreetAddress,
        city: location.Address.City,
        stateAbbreviation: location.Address.State,
        zip: location.Address.Zip,
        county: location.Address.County,
        countryAbbreviation: location.Address.Country,
      },
      coords: [
        Number.parseFloat(location.Coords.Lon),
        Number.parseFloat(location.Coords.Lat),
      ],
      isFavorite: location.Type === PlaceSetType.Favorite,
    };
    this.addStopMarkerService.addMarker(stopLocation);
    this.googleAnalytics.addGA(
      'Show_Custom_Location',
      'Custom_Location_Show',
      'Custom Place Search',
      'show location selected via custom place search'
    );
  }

  addSelectedPlace() {
    const addPlaces = this.places
      .find((place) =>
        place?.Places?.some((location) =>
          this.selectedCustomPlace?.child?.some(
            (selectedPlace) =>
              selectedPlace?.id === location?.Id && selectedPlace?.isSelected
          )
        )
      )
      .Places.filter((loc) =>
        this.selectedCustomPlace?.child?.some(
          (selPlace) => selPlace?.id === loc?.Id && selPlace?.isSelected
        )
      );
    this.selectedPlace.emit(addPlaces);
  }

  get selectedPlaceCount(): number {
    const selectCustomPlaces: ListItem[] =
      this.selectedCustomPlace?.child?.filter((place) =>
        Boolean(place?.isSelected)
      );
    return selectCustomPlaces?.length;
  }

  selectCustomPlace(item: ListItem): void {
    this.itemLevel = 0;
    if (!item.child) {
      item.isSelected = !item.isSelected;
      this.itemLevel += 1;
      return;
    }
    this.selectedCustomPlace = item;
    this.title = 'Custom Places';
    this.placeLists = this.getSearchableList(item?.child);
    this.itemLevel += 1;
    setTimeout(() => this.search?.nativeElement?.focus());
  }

  getSearchableList(items: ListItem[]): Observable<ListItem[]> {
    return new Observable((observer: Subscriber<string>) => {
      // Runs on every search
      observer.next(this.selectedValue);
    }).pipe(
      mergeMap((token: string) => this.getPlaceListsAsObservable(token, items))
    );
  }

  createPlaceSetState(places: UIPlace[]): ListItem[] {
    return places?.map((place: any) => {
      const primaryValues: string = place?.PlaceName;
      const secondaryValues: string[] = [
        place?.Address?.StreetAddress,
        place?.Address?.City,
        place?.Address?.State,
        place?.Address?.Zip,
      ].filter((value) => value);
      return {
        id: place?.Id,
        searchText: place?.DisplayText ? place.DisplayText : place?.PlaceName,
        toolTip: place?.IsShared ? 'Shared Location' : 'Private Location',
        primary: {
          textValue: primaryValues,
          textClass: 'font-weight-bold small mb-0 text-truncate mr-2',
          icon: place?.Places ? null : 'map_marker',
          iconpath: place?.Icon,
          count: place?.PlaceCounts,
        },
        secondary: {
          textValue: secondaryValues.join(', '),
          textClass: primaryValues
            ? 'font-weight-normal small text-truncate'
            : 'font-weight-bold small text-truncate',
        },
        tertiary: {
          textValue: place?.Address?.County
            ? `${place?.Address?.County} County`
            : null,
          textClass: 'text-gray-4 small text-truncate',
        },
        child: this.createPlaceSetState(place?.Places),
      };
    });
  }

  goBack(): void {
    this.itemLevel -= 1;
    this.selectedCustomPlace = null;
    this.title = 'Select a Custom Place Sets';
    this.placeLists = this.getSearchableList(this.listItems);
    setTimeout(() => this.search?.nativeElement?.focus());
  }

  getPlaceListsAsObservable(
    token: string,
    items: ListItem[]
  ): Observable<ListItem[]> {
    const query = new RegExp(token, 'i');
    const searchList: ListItem[] = items;
    const searchResult: ListItem[] = searchList.filter((place) =>
      query.test(place?.searchText)
        ? query.test(place?.searchText)
        : place?.child?.filter((value) => query.test(value?.searchText))
            ?.length > 0
    );

    let result: ListItem[];
    if (searchResult?.length) {
      this.hasResult = true;
      result = searchResult;
    } else {
      this.hasResult = false;
      result = [
        {
          primary: {
            textValue: 'No Match Results Found',
            textClass: 'font-weight-normal small',
          },
        },
      ];
    }
    this.search?.nativeElement?.focus();
    return of(result);
  }

  getCustomPlaces(): void {
    this.isLoadingResults = true;
    this.sm.manage = this.placeSetService.customPlaceStore$.subscribe(
      (combinedList) => {
        this.isLoadingResults = false;
        this.places = combinedList;
        this.listItems = combinedList?.length
          ? this.createPlaceSetState(this.places)
          : [
              {
                primary: {
                  textValue: 'No custom place sets found',
                  textClass: 'font-weight-normal small',
                },
              },
            ];
        this.placeLists = this.getSearchableList(this.listItems);
        setTimeout(() => this.search?.nativeElement?.focus());
      },
      (err) => {
        const errorItem: ListItem[] = [
          {
            primary: {
              textValue: 'Error Occured',
              textClass: 'font-weight-normal small',
            },
          },
        ];
        this.placeLists = of(errorItem);
        this.getSearchableList(this.listItems);
        setTimeout(() => this.search?.nativeElement?.focus());
        this.isLoadingResults = false;
      }
    );
  }

  ngOnDestroy(): void {
    this.sm.unsubscribe();
  }
}
